import { useCallback, useRef } from "react";
import { notificationApi } from "@services/notificationApi";
import { IntelligenceEngineApi } from "@src/services/extractionApi";
import {
  ProcessStatus,
  type INotificationMessage,
  type IProcessInfoViewModel,
} from "@models/index";
import { useBrowserNotifications } from "@hooks/useBrowserNotifications";
import { useTranslation } from "react-i18next";
import { getExtractionErrorMessage } from "@utils/errorUtil";

export const useProcessNotifications = (
  doctypeCode: string,
  intelligenceEngineApi: IntelligenceEngineApi,
  setProcessResultsInfo: React.Dispatch<
    React.SetStateAction<IProcessInfoViewModel[]>
  >,
  showError: (message: string) => void,
) => {
  const notifiedProcessIdsRef = useRef<Set<string>>(new Set());
  const activeWebSocketConnections = useRef<Set<string>>(new Set());
  const { showNotification } = useBrowserNotifications();
  const { t } = useTranslation();

  const handleProcessNotification = useCallback(
    async (processId: string, message: INotificationMessage) => {
      if (message.status === ProcessStatus.BatchCreated) {
        try {
          const extractedProcess =
            await intelligenceEngineApi.getExtractionProcessesInfo(processId);
          setProcessResultsInfo((prevResults) =>
            prevResults.map((res) =>
              res.id === processId ? extractedProcess : res,
            ),
          );
        } catch (error) {
          console.error(
            `Failed to fetch batch items for process ${processId}:`,
            error,
          );
        }
        return;
      }

      if (message.batch_item_id && message.batch_item_status) {
        setProcessResultsInfo((prevResults) =>
          prevResults.map((res) => {
            if (res.id === processId) {
              return {
                ...res,
                status: message.status || res.status,
                items: res.items.map((item) =>
                  item.id === message.batch_item_id
                    ? { ...item, status: message.batch_item_status! }
                    : item,
                ),
              };
            }
            return res;
          }),
        );
        return;
      }

      if (
        message.status === ProcessStatus.Completed ||
        message.status === ProcessStatus.PartialCompleted ||
        message.status === ProcessStatus.Failed
      ) {
        try {
          const finalViewModel =
            await intelligenceEngineApi.getExtractionProcessesInfo(processId);
          setProcessResultsInfo((prevResults) =>
            prevResults.map((res) =>
              res.id === processId ? finalViewModel : res,
            ),
          );

          if (!notifiedProcessIdsRef.current.has(processId)) {
            const isSuccess =
              message.status === ProcessStatus.Completed ||
              message.status === ProcessStatus.PartialCompleted;
            const name = finalViewModel.isBatch
              ? `Batch-${processId.split("-")[0]}`
              : finalViewModel.items[0]?.fileName || processId.split("-")[0];
            const title = isSuccess
              ? "Extraction Completed"
              : "Extraction Failed";
            const body = isSuccess
              ? `${name} is ready to view.`
              : `${name} could not be processed.`;

            showNotification(title, { body, tag: processId });
            notifiedProcessIdsRef.current.add(processId);

            if (!isSuccess && finalViewModel.errorMessage) {
              const { message, suggestion } = getExtractionErrorMessage(
                finalViewModel.errorMessage,
                t,
              );
              showError(`${message} ${suggestion}`);
            }
          }
        } catch (error) {
          console.error(
            `Failed to fetch final details for process ${processId}:`,
            error,
          );
          setProcessResultsInfo((prevResults) =>
            prevResults.map((res) =>
              res.id === processId ? { ...res, status: message.status! } : res,
            ),
          );
        } finally {
          notificationApi.disconnect(processId);
          activeWebSocketConnections.current.delete(processId);
        }
      } else if (message.status) {
        setProcessResultsInfo((prevResults) =>
          prevResults.map((res) =>
            res.id === processId ? { ...res, status: message.status! } : res,
          ),
        );
      }
    },
    [intelligenceEngineApi, setProcessResultsInfo, showNotification, showError, t],
  );

  const connectToProcessNotifications = useCallback(
    (processId: string) => {
      if (!doctypeCode || activeWebSocketConnections.current.has(processId)) {
        return;
      }

      notificationApi.connectToNotifications(doctypeCode, processId, {
        onMessage: (message) => {
          handleProcessNotification(processId, message);
        },
        onError: (error) => {
          console.error(`WebSocket error for process ${processId}:`, error);
          activeWebSocketConnections.current.delete(processId);
        },
        onClose: () => {
          activeWebSocketConnections.current.delete(processId);
        },
        onOpen: () => {
          activeWebSocketConnections.current.add(processId);
        },
      });
    },
    [doctypeCode, handleProcessNotification],
  );

  /**
   * Connect and wait for WebSocket authentication before resolving.
   * Used during file upload to ensure the connection is ready before
   * the S3 upload triggers backend processing.
   */
  const connectAndWaitForAuth = useCallback(
    async (processId: string): Promise<void> => {
      if (!doctypeCode || activeWebSocketConnections.current.has(processId)) {
        return;
      }

      await notificationApi.connectAndWaitForAuth(doctypeCode, processId, {
        onMessage: (message) => {
          handleProcessNotification(processId, message);
        },
        onError: (error) => {
          console.error(`WebSocket error for process ${processId}:`, error);
          activeWebSocketConnections.current.delete(processId);
        },
        onClose: () => {
          activeWebSocketConnections.current.delete(processId);
        },
        onOpen: () => {
          activeWebSocketConnections.current.add(processId);
        },
      });
    },
    [doctypeCode, handleProcessNotification],
  );

  return {
    connectToProcessNotifications,
    connectAndWaitForAuth,
  };
};
